import { motion } from "motion/react";
import type React from "react";
import { useTranslation } from "react-i18next";

const imgLogo = "/img/logo_word_color.svg";
const imgTeamLight = "/img/event-page/team_light.svg";
const imgTeamDark = "/img/event-page/team_dark.svg";
const imgVsBadge = "/img/event-page/vs_badge.svg";

interface CountdownScreenProps {
	timeLeft?: string;
	teamNameLight?: string;
	teamNameDark?: string;
}
export const CountdownScreen: React.FC<CountdownScreenProps> = ({
	timeLeft = "0:60",
	teamNameLight,
	teamNameDark,
}) => {
	const { t } = useTranslation();

	const lightName = teamNameLight || t("guest.team_setup.default_light");
	const darkName = teamNameDark || t("guest.team_setup.default_dark");

	return (
		<div className="bg-white flex flex-col items-center justify-between min-h-screen p-6 relative w-full mx-auto overflow-hidden">
			{/* Logo */}
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className="w-[109px] h-[24px] mt-6"
			>
				<img
					alt="inJOY Logo"
					className="w-full h-full object-contain"
					src={imgLogo}
				/>
			</motion.div>

			{/* Center Content */}
			<div className="flex flex-col items-center justify-center py-5 flex-1 w-full">
				<motion.div
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.5, delay: 0.2 }}
					className="flex flex-col items-center gap-7 w-full max-w-[358px]"
				>
					{/* Countdown Info */}
					<div className="flex flex-col items-center gap-3 w-full text-center">
						<div className="flex flex-col gap-1 w-full font-medium text-[#404040] tracking-[-0.5px]">
							<p className="text-[28px] leading-[1.2]">
								{t("guest.countdown.starts_in")}
							</p>
							<p className="text-[60px] leading-none">{timeLeft}</p>
						</div>
						<p className="text-[16px] leading-[1.4] tracking-[0.25px] text-[#6d6d6d]">
							{t("guest.countdown.look_at_screen")}
						</p>
					</div>

					{/* Team Cards */}
					<div className="flex gap-2 h-[205px] items-center relative w-full">
						{/* Light Team */}
						<div className="flex-1 h-full rounded-lg border border-[#dfdfdf] bg-white flex flex-col items-center justify-center gap-2 px-3 py-5">
							<div className="w-[80px] h-[80px] rounded-full border border-white/20 bg-white shrink-0">
								<img
									alt={lightName}
									className="w-full h-full"
									src={imgTeamLight}
								/>
							</div>
							<p className="font-medium text-[16px] leading-[1.35] text-[#404040] text-center overflow-hidden text-ellipsis w-full">
								{lightName}
							</p>
						</div>

						{/* Dark Team */}
						<div className="flex-1 h-full rounded-lg border border-[#dfdfdf] flex flex-col items-center justify-center gap-2 px-3 py-5">
							<div className="w-[80px] h-[80px] rounded-full border border-white/20 bg-white shrink-0">
								<img
									alt={darkName}
									className="w-full h-full"
									src={imgTeamDark}
								/>
							</div>
							<p className="font-medium text-[16px] leading-[1.35] text-[#404040] text-center overflow-hidden text-ellipsis w-full">
								{darkName}
							</p>
						</div>

						{/* VS Badge */}
						<img
							src={imgVsBadge}
							alt="VS"
							className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[48px] h-[60px]"
						/>
					</div>
				</motion.div>
			</div>
		</div>
	);
};
